/**
 * FilterBar configuration builder for the Inspection Series listing.
 *
 * Produces a FilterConfig[] array that represents every current Series filter,
 * respecting:
 *  - context-hidden rules (building hidden in building scope;
 *    account hidden in account scope)
 *  - global-territory gate (territory hidden when hasGlobalTerritoriesApplied())
 *  - prop flags: hasShowBuildingFilter, hasShowAccountFilter,
 *    hasShowExpirationDateFilter, hasShowStatusFilter, hasShowAutoExtendFilter,
 *    hasShowExpiredSeriesFilter, hasDisableAllFilters
 *  - Unassigned sentinel for territory (endpoint-compatible value: 0)
 *  - Building subtitle (address) via Option.subtitle
 *  - PagedMultiSelect loaders for building, account, territory
 *
 * Series-specific adaptations (task-01 inventory #4):
 *  - Filters are ALWAYS visible — there is no active/inactive toggle UI.
 *  - All FilterConfig entries have `active: true` (always rendered in the filter bar).
 *  - No `toggleItems` functionality is needed; the field is included for API consistency
 *    but the toggle list will be empty.
 *
 * Returns:
 *  - filters: FilterConfig[] — pass directly to <FilterBar filters={filters} />
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import { withUnassignedFirstPage, withUnassignedSelected } from '@helpers/unassignedLookup';
import { translate } from '@utils/i18n';
import { INSPECTIONS_DATE_RANGE_PRESETS, CUSTOM_RANGE_TYPE, toFilterSelectedPresetId, fromFilterPresetId, computeRangeForPresetId } from '../../../InspectionsIndex/adapters/inspectionsDateRangePresets';
const SERIES_DATATABLE_NS = 'inspection_series.datatable';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Formats a Date as M/D/YYYY — matches Inspections listing date persistence (no UTC shift). */
function formatLocalDate(date) {
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}
/**
 * Converts a stored date string to a Date in local time.
 * Accepts US format (M/D/YYYY) and ISO YYYY-MM-DD (legacy persisted preferences).
 */
function toDate(value) {
    if (!value)
        return null;
    const usMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
    if (usMatch) {
        const month = Number(usMatch[1]);
        const day = Number(usMatch[2]);
        const year = Number(usMatch[3]);
        const date = new Date(year, month - 1, day);
        return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
    }
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!isoMatch)
        return null;
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    const day = Number(isoMatch[3]);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}
/** Convert DateRange filter value → SeriesDateRange for onChange (calendar edits = custom_range). */
function fromDateRange(value) {
    const hasDate = value.start != null || value.end != null;
    return {
        start: value.start ? formatLocalDate(value.start) : null,
        end: value.end ? formatLocalDate(value.end) : null,
        rangeType: hasDate ? CUSTOM_RANGE_TYPE : null
    };
}
/** Convert a M/D/YYYY date string (from computeRangeForPresetId) to ISO YYYY-MM-DD. */
function mdyToIso(mdy) {
    if (!mdy)
        return null;
    const parts = mdy.split('/');
    if (parts.length !== 3)
        return null;
    const [m, d, y] = parts;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}
// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
/**
 * Builds the FilterBar configuration for the Inspection Series listing.
 *
 * All returned FilterConfig entries have `active: true` — Series filters are
 * always visible and do not use the active/inactive toggle mechanism that
 * Inspections uses.  The prop flags (hasShowXxxFilter) gate each filter's
 * inclusion in the list entirely.
 *
 * When `hasDisableAllFilters` is true, an empty filter list is returned.
 */
export function useSeriesFilterConfig(opts) {
    const { scope, filters, onChange, hasShowBuildingFilter, hasShowAccountFilter, hasShowExpirationDateFilter, hasShowStatusFilter, hasShowAutoExtendFilter, hasShowExpiredSeriesFilter, hasDisableAllFilters, loadBuildings, loadAccounts, loadTerritories, loadSelectedBuildings, loadSelectedAccounts, loadSelectedTerritories } = opts;
    if (hasDisableAllFilters) {
        return { filters: [] };
    }
    const isBuildingScope = scope.type === 'building';
    const isAccountScope = scope.type === 'account';
    const globalTerritoriesApplied = hasGlobalTerritoriesApplied();
    // Helper to update a single filter field
    const set = (field, value) => {
        onChange(Object.assign(Object.assign({}, filters), { [field]: value }));
    };
    const filterList = [];
    // --- building (paged multi-select with address subtitle) ---
    // Hidden in building scope per hasShowBuildingFilter=false from the ERB mount.
    // The prop flag already encodes the scope gate, so we respect it directly.
    if (hasShowBuildingFilter && !isBuildingScope) {
        filterList.push({
            type: 'pagedMultiSelect',
            field: 'building',
            label: 'Building',
            active: true,
            value: filters.building,
            loadOptions: loadBuildings,
            loadSelectedValues: loadSelectedBuildings,
            onChange: (v) => set('building', v)
        });
    }
    // --- account (paged multi-select) ---
    // Hidden in account scope per hasShowAccountFilter=false from the ERB mount.
    if (hasShowAccountFilter && !isAccountScope) {
        filterList.push({
            type: 'pagedMultiSelect',
            field: 'account',
            label: 'Account',
            active: true,
            value: filters.account,
            loadOptions: loadAccounts,
            loadSelectedValues: loadSelectedAccounts,
            onChange: (v) => set('account', v)
        });
    }
    // --- territory (paged multi-select with Unassigned sentinel) ---
    // Hidden when global territories are applied (same gate as Inspections).
    // The ERB mount does not have a separate hasShowTerritoryFilter prop;
    // the territory select is shown based on !hasGlobalTerritoriesApplied.
    if (!globalTerritoriesApplied) {
        filterList.push({
            type: 'pagedMultiSelect',
            field: 'territory',
            label: 'Territory',
            active: true,
            value: filters.territory,
            loadOptions: (query, cursor) => __awaiter(this, void 0, void 0, function* () {
                return withUnassignedFirstPage(yield loadTerritories(query, cursor), query, cursor);
            }),
            loadSelectedValues: withUnassignedSelected(loadSelectedTerritories),
            onChange: (v) => set('territory', v)
        });
    }
    // --- expirationDate (date range) ---
    if (hasShowExpirationDateFilter) {
        filterList.push({
            type: 'dateRange',
            field: 'expirationDate',
            label: 'Expiration Date',
            active: true,
            value: { start: toDate(filters.expirationDate.start), end: toDate(filters.expirationDate.end) },
            presets: INSPECTIONS_DATE_RANGE_PRESETS,
            selectedPresetId: toFilterSelectedPresetId(filters.expirationDate.rangeType),
            onPresetSelect: (id) => {
                const rangeType = fromFilterPresetId(id);
                if (rangeType === CUSTOM_RANGE_TYPE) {
                    set('expirationDate', {
                        start: filters.expirationDate.start,
                        end: filters.expirationDate.end,
                        rangeType: CUSTOM_RANGE_TYPE
                    });
                }
                else {
                    const range = computeRangeForPresetId(id);
                    set('expirationDate', { start: mdyToIso(range.start), end: mdyToIso(range.end), rangeType });
                }
            },
            onChange: (v) => set('expirationDate', fromDateRange(v))
        });
    }
    // --- status (boolean select: null = all, true = cancelled, false = active) ---
    if (hasShowStatusFilter) {
        filterList.push({
            type: 'boolean',
            field: 'status',
            label: translate('filters.status', { namespace: SERIES_DATATABLE_NS }),
            active: true,
            value: filters.status,
            onChange: (v) => set('status', v)
        });
    }
    // --- autoExtend (boolean select) ---
    if (hasShowAutoExtendFilter) {
        filterList.push({
            type: 'boolean',
            field: 'autoExtend',
            label: 'Auto Extend',
            active: true,
            value: filters.autoExtend,
            onChange: (v) => set('autoExtend', v)
        });
    }
    // --- expiredSeries: false = non-expired, true = expired only, null = All ---
    // persistAllSelection keeps the chip labeled "All" when null is an explicit choice.
    if (hasShowExpiredSeriesFilter) {
        filterList.push({
            type: 'boolean',
            field: 'expiredSeries',
            label: 'Expired Series',
            active: true,
            persistAllSelection: true,
            value: filters.expiredSeries,
            onChange: (v) => set('expiredSeries', v)
        });
    }
    return { filters: filterList };
}
